import React from 'react';
import UpdateLayout, { ExternalLink } from './UpdateLayout';
import { DELIVERY_CONTEXT, FABRIC_MIRRORING_AS_OF } from '../../../content/snowflakeUpdates';

const MIRRORING_DOC = 'https://learn.microsoft.com/en-us/fabric/mirroring/snowflake';
const MIRRORING_TUTORIAL = 'https://learn.microsoft.com/en-us/fabric/mirroring/snowflake-tutorial';
const ICEBERG_GA_NOTE =
  'https://docs.snowflake.com/en/release-notes/2026/other/2026-01-30-iceberg-microsoft-fabric-bidirectional-data-access-ga';
const ICEBERG_GA_BLOG =
  'https://blog.fabric.microsoft.com/en/blog/microsoft-onelake-and-snowflake-interoperability-is-now-generally-available';
const SNOWFLAKE_DATABASE_ITEM =
  'https://learn.microsoft.com/en-us/fabric/onelake/snowflake/create-snowflake-database-item';
const EXTENDED_MIRRORING =
  'https://blog.fabric.microsoft.com/en/blog/extended-capabilities-in-mirroring-in-microsoft-fabric-optional-enhancements-to-core-mirroring';

const SOURCES = [
  {
    href: MIRRORING_DOC,
    label: 'Mirroring Snowflake (Microsoft Learn, accessed Oct 20, 2026)',
  },
  {
    href: MIRRORING_TUTORIAL,
    label: 'Snowflake mirroring tutorial (Microsoft Learn, accessed Oct 20, 2026)',
  },
  {
    href: ICEBERG_GA_NOTE,
    label: 'Jan 30, 2026: Iceberg and Microsoft Fabric bidirectional data access is GA',
  },
  {
    href: ICEBERG_GA_BLOG,
    label: 'Feb 3, 2026: OneLake and Snowflake interoperability is generally available',
  },
  {
    href: SNOWFLAKE_DATABASE_ITEM,
    label: 'Create a Snowflake database item in OneLake',
  },
  {
    href: EXTENDED_MIRRORING,
    label: 'Mar 19, 2026: Extended capabilities in Mirroring (preview)',
  },
];

const h2Class = 'pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900';

export default function FabricMirroringPost() {
  return (
    <UpdateLayout
      path="/updates/snowflake-microsoft-fabric-mirroring-vs-iceberg"
      title="Mirroring vs. Iceberg interop: using Snowflake data in Microsoft Fabric (2026 guide)"
      chip="Integration"
      asOf={FABRIC_MIRRORING_AS_OF}
      sources={SOURCES}
    >
      <h2 id="the-two-routes" className={h2Class}>
        The two routes in plain terms
      </h2>
      <p>
        Mirroring keeps a copy of Snowflake tables in Fabric. Microsoft&apos;s documentation does not
        mark it as a preview feature.
        It continuously replicates Snowflake tables into OneLake as <code>Delta/Parquet</code>, and
        each mirrored database gets a read-only SQL analytics endpoint. Replication is continuous,
        with no schedule option (
        <ExternalLink href={MIRRORING_DOC}>Mirroring Snowflake</ExternalLink>
        ). When nothing changes, polling backs off to as slow as once an hour (
        <ExternalLink href={MIRRORING_TUTORIAL}>mirroring tutorial</ExternalLink>
        ).
      </p>
      <p>
        Apache Iceberg interop is the shared-table route, and it is generally available. OneLake and
        Snowflake read the same Iceberg tables. Snowflake can write Iceberg tables directly into
        OneLake, so there is a single copy (
        <ExternalLink href={ICEBERG_GA_NOTE}>Jan 30, 2026 release note</ExternalLink>
        , <ExternalLink href={ICEBERG_GA_BLOG}>Feb 3, 2026 Fabric blog</ExternalLink>
        , <ExternalLink href={SNOWFLAKE_DATABASE_ITEM}>Snowflake database item</ExternalLink>
        ).
      </p>
      <p>
        Optional extended mirroring capabilities, with Snowflake as the first source, are in preview (
        <ExternalLink href={EXTENDED_MIRRORING}>Mar 19, 2026</ExternalLink>
        ).
      </p>

      <h2 id="what-mirrors" className={h2Class}>
        What mirrors and what doesn&apos;t
      </h2>
      <p>
        Support depends on the table type (
        <ExternalLink href={MIRRORING_DOC}>Mirroring Snowflake</ExternalLink>
        ).
      </p>
      <div
        className="overflow-x-auto focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-700"
        tabIndex={0}
        role="region"
        aria-label="What Snowflake mirroring replicates, by table type"
      >
        <table className="w-full border-collapse text-left text-sm">
          <caption className="mb-3 text-left text-sm text-ink-600">
            What Snowflake mirroring replicates, by table type.
          </caption>
          <thead>
            <tr className="border-b border-brand-200 text-brand-900">
              <th scope="col" className="py-2 pr-4 font-semibold">
                Table type
              </th>
              <th scope="col" className="py-2 font-semibold">
                What mirroring does
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-brand-100 align-top">
              <th scope="row" className="py-2 pr-4 font-semibold text-ink-900">
                Regular tables
              </th>
              <td className="py-2">Fully supported</td>
            </tr>
            <tr className="border-b border-brand-100 align-top">
              <th scope="row" className="py-2 pr-4 font-semibold text-ink-900">
                Iceberg tables
              </th>
              <td className="py-2">Need a storage connection</td>
            </tr>
            <tr className="border-b border-brand-100 align-top">
              <th scope="row" className="py-2 pr-4 font-semibold text-ink-900">
                Views and materialized views
              </th>
              <td className="py-2">Refresh every 12 hours</td>
            </tr>
            <tr className="border-b border-brand-100 align-top">
              <th scope="row" className="py-2 pr-4 font-semibold text-ink-900">
                External, transient, temporary, and dynamic tables
              </th>
              <td className="py-2">Not mirrored</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 id="where-costs-come-from" className={h2Class}>
        Where costs come from and how to keep them down
      </h2>
      <p>
        Fabric does not charge for replication compute, and storage is free up to a capacity-based
        limit. Snowflake still bills warehouse compute and cloud-services credits when changes are
        read (
        <ExternalLink href={MIRRORING_DOC}>Mirroring Snowflake</ExternalLink>
        ).
      </p>
      <p>
        Microsoft suggests reusing an existing warehouse and mirroring only the tables you need.
        Schema changes can trigger a full reload, so watch dbt schema changes that can force that
        reload (
        <ExternalLink href={MIRRORING_DOC}>Mirroring Snowflake</ExternalLink>
        ).
      </p>

      <h2 id="security-and-networking" className={h2Class}>
        Security and networking caveats
      </h2>
      <p>
        Security set up in Snowflake must be recreated in Fabric. Private Link is not supported, so
        a private network setup needs a gateway (
        <ExternalLink href={MIRRORING_TUTORIAL}>mirroring tutorial</ExternalLink>
        ).
      </p>
      <p>
        Authentication can be username/password, Entra SSO, or a key pair. Workspace identity is not
        supported (
        <ExternalLink href={MIRRORING_DOC}>Mirroring Snowflake</ExternalLink>
        ).
      </p>

      <div className="rounded-xl border border-brand-200 bg-brand-50 p-6">
        <h2 id="mirroring-or-iceberg" className={h2Class}>
          Mirroring or Iceberg
        </h2>
        <ol className="mt-4 list-decimal space-y-3 pl-5">
          <li>
            <span className="font-semibold text-ink-900">Audit table types first.</span> Regular
            tables are fully supported. Iceberg tables need a storage connection. Views and
            materialized views refresh every 12 hours. External, transient, temporary, and dynamic
            tables are not mirrored (
            <ExternalLink href={MIRRORING_DOC}>Mirroring Snowflake</ExternalLink>
            ).
          </li>
          <li>
            <span className="font-semibold text-ink-900">
              Choose mirroring for a Fabric-native copy with low setup effort.
            </span>{' '}
            Mirroring replicates Snowflake tables into OneLake as Delta/Parquet and adds a read-only
            SQL analytics endpoint on each mirrored database (
            <ExternalLink href={MIRRORING_DOC}>Mirroring Snowflake</ExternalLink>
            ).
          </li>
          <li>
            <span className="font-semibold text-ink-900">
              Choose Iceberg for a single copy and less duplication.
            </span>{' '}
            Both platforms read the same Iceberg tables, and Snowflake can write Iceberg tables
            directly into OneLake (
            <ExternalLink href={ICEBERG_GA_NOTE}>Jan 30, 2026 release note</ExternalLink>
            , <ExternalLink href={ICEBERG_GA_BLOG}>Feb 3, 2026 Fabric blog</ExternalLink>
            ).
          </li>
        </ol>
      </div>

      <h2 id="data-infrastructure-practice" className={h2Class}>
        Data-infrastructure practice
      </h2>
      <p>
        Manage the Snowflake connection and the mirroring configuration as code with Terraform.
        Promote those changes through CI/CD, with Azure DevOps Pipelines or GitHub Actions.
        Coordinate dbt deploys with mirroring, because a schema change can trigger a full reload (
        <ExternalLink href={MIRRORING_DOC}>Mirroring Snowflake</ExternalLink>
        ). {DELIVERY_CONTEXT}
      </p>

      <h2 id="how-myorglab-helps" className={h2Class}>
        How MyOrgLab helps
      </h2>
      <p>
        MyOrgLab builds governed analytics in the customer&apos;s own account, including the
        connection and the configuration for the route you choose.
      </p>
    </UpdateLayout>
  );
}
